import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { parseLabels, activeLabel, removeLabel, replaceLabel } from '../common/utils/labelQuery.mjs';
import tagLabel from '../common/helpers/tagLabel';
export default function addLabelSearch() {
  override(app.search.gambits, 'apply', function (original, type, filter) {
    if (['discussions', 'posts'].includes(type) && typeof filter.q === 'string') {
      const parsed = parseLabels(filter.q);
      if (parsed.labels.length) {
        const existing = filter.labels ? Array.isArray(filter.labels) ? filter.labels : String(filter.labels).split(',') : [];
        filter = {
          ...filter,
          q: parsed.q,
          labels: [...new Set([...existing, ...parsed.labels])]
        };
      }
    }
    return original(type, filter);
  });

  // Suppress global error banners for preview requests; the modal reports the error inline.
  for (const type of ['Discussions', 'Posts']) {
    override(`flarum/forum/components/Global${type}SearchSource`, 'search', async function (original, query, limit) {
      if (!parseLabels(query).labels.length) return original(query, limit);
      const key = query.toLowerCase();
      this.results.set(key, []);
      const include = this.resource === 'posts' ? 'user,discussion.tags' : 'mostRelevantPost,user,firstPost,tags';
      const result = await app.store.find(this.resource, {
        filter: {
          q: query
        },
        page: {
          limit
        },
        include
      }, undefined, {
        errorHandler: () => false
      });
      this.results.set(key, result);
      m.redraw();
    });
  }
  const modal = 'flarum/common/components/SearchModal';
  extend(modal, 'oninit', function () {
    this.labelSearch = {
      generation: 0,
      active: null,
      matches: [],
      busy: false,
      error: ''
    };
  });
  override(modal, 'search', function (original, query) {
    const state = this.labelSearch;
    if (!state) return original(query);
    clearTimeout(this.searchTimeout);
    const generation = ++state.generation;
    state.active = activeLabel(query, this.inputElement()[0]?.selectionStart ?? query.length);
    state.matches = [];
    state.busy = false;
    state.error = '';
    this.loadingSources = [];
    if (parseLabels(query).labels.length && this.activeSource().resource === 'users') {
      this.activeSource(this.sources.find(source => source.resource === 'discussions') || this.sources[0]);
    }
    if (state.active) {
      if (state.active.term.length < 2) {
        m.redraw();
        return;
      }
      state.busy = true;
      this.searchTimeout = setTimeout(async () => {
        try {
          const results = await app.store.find('tags', {
            filter: {
              q: state.active.term
            },
            page: {
              limit: 50
            }
          }, undefined, {
            errorHandler: () => false
          });
          if (state.generation === generation) state.matches = results;
        } catch (_) {
          if (state.generation === generation) state.error = 'Label search failed. Please try again.';
        } finally {
          if (state.generation === generation) {
            state.busy = false;
            m.redraw();
          }
        }
      }, 250);
      m.redraw();
      return;
    }
    if (!query.trim()) {
      m.redraw();
      return;
    }
    const source = this.activeSource();
    this.searchTimeout = setTimeout(async () => {
      this.loadingSources = [source.resource];
      m.redraw();
      try {
        await source.search(query, 6);
      } catch (error) {
        if (state.generation === generation) state.error = error.response?.errors?.[0]?.detail || 'Search failed. Please try again.';
      } finally {
        if (state.generation === generation) {
          this.loadingSources = [];
          m.redraw();
        }
      }
    }, 250);
  });
  extend(modal, 'tabItems', function (items) {
    if (parseLabels(this.query()).labels.length) items.remove('users');
  });
  override(modal, 'gambits', function (original) {
    return this.labelSearch?.active ? [] : original();
  });
  extend(modal, 'activeTabItems', function (items) {
    const state = this.labelSearch;
    if (!state) return;
    const parsed = parseLabels(this.query());
    const setQuery = value => {
      this.query(value);
      this.inputElement().val(value).focus();
      this.inputElement()[0]?.setSelectionRange(value.length, value.length);
      this.search(value);
    };
    items.add('labels-help', m("p", {
      className: "helpText"
    }, "Use #label to filter discussions and posts. Multiple labels mean \u201Cany of these.\u201D"), 130);
    if (parsed.labels.length) items.add('labels-selected', m("div", {
      className: "nightash-selected",
      "aria-label": "Selected label filters"
    }, parsed.labels.map(slug => m(Button, {
      type: "button",
      className: "Button",
      "aria-label": `Remove #${slug}`,
      onclick: () => setQuery(removeLabel(this.query(), slug))
    }, "#", slug, " \xD7"))), 120);
    if (state.error) {
      items.remove('results');
      items.remove('fullPageLink');
      items.add('labels-error', m("p", {
        role: "alert"
      }, state.error), 110);
    }
    if (state.active) {
      items.remove('results');
      items.remove('fullPageLink');
      items.remove('gambits');
      items.add('labels-suggestions', m("section", {
        "aria-label": "Matching labels"
      }, state.busy ? m(LoadingIndicator, null) : m('[', null, state.active.term.length < 2 ? m("p", null, "Type at least two characters after #.") : !state.matches.length && !state.error ? m("p", null, "Label not found or unavailable.") : null, m("ul", {
        className: "Dropdown-menu nightash-search-labels"
      }, state.matches.map(tag => m("li", null, m(Button, {
        type: "button",
        className: "Button Button--link",
        onclick: () => setQuery(replaceLabel(this.query(), state.active, tag.slug()))
      }, tagLabel(tag), " ", m("small", null, "#", tag.slug()))))))), 100);
    }
  });
  extend(modal, 'onremove', function () {
    clearTimeout(this.searchTimeout);
    if (this.labelSearch) ++this.labelSearch.generation;
  });
  extend(IndexPage.prototype, 'contentItems', function (items) {
    const params = app.search.state.params();
    const selected = params.filter?.labels;
    const labels = selected ? Array.isArray(selected) ? selected : String(selected).split(',') : [];
    if (!labels.length) return;
    items.add('label-filters', m("div", {
      className: "nightash-search-filters"
    }, m("span", null, "In any of: "), labels.map(slug => m(Button, {
      className: "Button",
      "aria-label": `Remove #${slug}`,
      onclick: () => {
        const filter = {
          ...params.filter
        };
        filter.labels = labels.filter(label => label !== slug);
        if (!filter.labels.length) delete filter.labels;
        m.route.set(app.route('index', {
          ...params,
          filter
        }));
      }
    }, "#", slug, " \xD7"))), 95);
  });
}